import React, { useState } from "react";
import MaterialTable from "material-table";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogContent, DialogActions, Chip, Typography, Divider, Tooltip, Grid } from "@material-ui/core";
import Button from "components/CustomButtons/Button.js";
import SnackBarWiiba from "components/Snackbar/SnackBarWiiba";
import useMessage from "../../hooks/useMessage";
import { pdf } from "@react-pdf/renderer";
import AmortizacionPDF from "components/Contract/AmortizacionPDF";

const useStyles = makeStyles(theme => ({
  header: {
    background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
    padding: "14px 24px",
    display: "flex", alignItems: "center", justifyContent: "space-between"
  },
  headerTitle: { color: "#fff", fontSize: 16, fontWeight: 700, margin: 0 },
  headerSub: { color: "#cce5ff", fontSize: 11, margin: 0 },
  content: { padding: "16px 24px", backgroundColor: "#f8f9fa" },
  card: {
    backgroundColor: "#fff", borderRadius: 8, padding: "16px 20px",
    marginBottom: 16, border: "1px solid #e0e0e0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
  },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, color: "#1565c0", textTransform: "uppercase",
    letterSpacing: 1, marginBottom: 10
  },
  divider: { marginBottom: 12 },
  actions: {
    padding: "12px 24px", borderTop: "1px solid #e0e0e0",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    backgroundColor: "#fff"
  },
  actionsRight: { display: "flex", gap: 8 },
  summaryChip: { fontWeight: 600, fontSize: 12 }
}));

const ESTATUS_LABELS = { A: "Pagado", P: "Pendiente", M: "Moratorio" };
const ESTATUS_COLORS = { A: "#4caf50", P: "#1565c0", M: "#e53935" };
const fmtMoney = val => "$" + parseFloat(val || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 });
const fmtDate = str => {
  if (!str) return "—";
  // Formato dd-mm-yyyy del SP
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [d, m, y] = str.split("-");
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(str);
  if (isNaN(dt)) return str;
  return dt.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Campos en MAYÚSCULAS porque el SP hace UPPER() del JSON
  const columnsAmort = [
  {
    title: "No.", field: "ID", width: "6%",
    headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center", fontWeight: 600 },
    // ID es el índice del loop en el SP (0-based), mostramos +1
    render: r => (r.ID !== undefined && r.ID !== null) ? r.ID + 1 : "—"
  },
  {
    title: "Estatus", field: "ESTATUS", width: "14%",
    headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center" },
    render: r => {
      const ant = parseFloat(r.ANTICIPADO) || 0;
      const label = ESTATUS_LABELS[r.ESTATUS] || r.ESTATUS;
      const bg = ESTATUS_COLORS[r.ESTATUS] || "#999";
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <Chip label={label} size="small" style={{ backgroundColor: bg, color: "#fff", fontSize: 10 }} />
          {r.ESTATUS !== "A" && ant > 0 && (
            <span style={{ fontSize: 9, color: "#ff9800", fontWeight: 600 }}>
              {fmtMoney(ant)} abonado
            </span>
          )}
        </div>
      );
    }
  },
  {
    title: "Fecha a pagar", field: "FECHAAPAGAR", width: "15%",
    headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center" },
    render: r => fmtDate(r.FECHAAPAGAR)
  },
  {
    title: "Fecha de pago", field: "FECHAPAGO", width: "15%",
    headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center" },
    render: r => r.FECHAPAGO ? fmtDate(r.FECHAPAGO) : "—"
  },
  {
    title: "Monto", field: "MONTOAPAGAR", width: "15%",
    headerStyle: { textAlign: "right" }, cellStyle: { textAlign: "right" },
    render: r => fmtMoney(r.MONTOAPAGAR)
  },
  {
    title: "Abonado", field: "ANTICIPADO", width: "12%",
    headerStyle: { textAlign: "right" }, cellStyle: { textAlign: "right" },
    render: r => {
      const a = parseFloat(r.ANTICIPADO) || 0;
      if (a === 0) return "—";
      return <span style={{ color: "#ff9800", fontWeight: 500 }}>{fmtMoney(a)}</span>;
    }
  },
  {
    title: "Por pagar", width: "15%",
    headerStyle: { textAlign: "right" }, cellStyle: { textAlign: "right", fontWeight: 600 },
    render: r => {
      if (r.ESTATUS === "A") {
        const ant = parseFloat(r.ANTICIPADO) || 0;
        const monto = parseFloat(r.MONTOAPAGAR) || 0;
        // Si tuvo abonos, mostrar cuánto se pagó al final para completar
        if (ant > 0) return <span style={{ color: "#4caf50" }}>Pagado (cierre: {fmtMoney(monto - ant)})</span>;
        return <span style={{ color: "#4caf50" }}>Pagado</span>;
      }
      const saldo = (parseFloat(r.MONTOAPAGAR) || 0) - (parseFloat(r.ANTICIPADO) || 0);
      return <span style={{ color: "#1565c0" }}>{fmtMoney(saldo)}</span>;
    }
  }
];

const columnsMov = [
  {
    title: "Tipo", field: "NATURALEZA", width: "10%",
    headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center" },
    render: r => (
      <Chip label={r.NATURALEZA === "E" ? "Entrada" : "Salida"} size="small"
        style={{ backgroundColor: r.NATURALEZA === "E" ? "#e8f5e9" : "#ffebee",
          color: r.NATURALEZA === "E" ? "#2e7d32" : "#c62828", fontSize: 10 }} />
    )
  },
  {
    title: "Concepto", field: "CONCEPTO", width: "20%",
    headerStyle: { textAlign: "left" }, cellStyle: { textAlign: "left", fontSize: 12 }
  },
  {
    title: "Monto", field: "MONTO", width: "15%",
    headerStyle: { textAlign: "right" }, cellStyle: { textAlign: "right", fontWeight: 600 },
    render: r => <span style={{ color: "#1565c0" }}>{fmtMoney(r.MONTO)}</span>
  },
  {
    title: "Fecha", field: "FECHA_OPERACION", width: "15%",
    headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center" },
    render: r => fmtDate(r.FECHA_OPERACION)
  },
  {
    title: "Método de pago", field: "METODO_PAGO", width: "15%",
    headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center", fontSize: 12 }
  },
  { title: "Referencia", field: "REFERENCIA", width: "25%" }
];

const ModalMovimientosLotes = ({
  modalMov, movimientosLote, setMovimientosLote, setModalMov,
  amortizacionxlote, setAmortizacionxLote, idContrato
}) => {
  const classes = useStyles();
  const [, setSelectedRow] = useState();
  const { opensnack, typesnackbar, message, handleMuestraSnack, handleClosesSnack } = useMessage();

  const handleClose = () => {
    setModalMov(false);
    setMovimientosLote([]);
    setAmortizacionxLote([]);
  };

  // Cálculos correctos
  const totalContrato = (amortizacionxlote || [])
    .reduce((s, r) => s + (parseFloat(r.MONTOAPAGAR) || 0), 0);

  const totalPagado = (amortizacionxlote || []).reduce((s, r) => {
    if (r.ESTATUS === "A") return s + (parseFloat(r.MONTOAPAGAR) || 0);
    return s + (parseFloat(r.ANTICIPADO) || 0); // abonos parciales de pendientes
  }, 0);

  const totalPendiente = totalContrato - totalPagado;

  // Totales de movimientos
  const totalEntradas = (movimientosLote || [])
    .filter(m => m.NATURALEZA === "E")
    .reduce((s, m) => s + (parseFloat(m.MONTO) || 0), 0);
  const totalSalidas = (movimientosLote || [])
    .filter(m => m.NATURALEZA !== "E")
    .reduce((s, m) => s + (parseFloat(m.MONTO) || 0), 0);
  const saldoMovimientos = totalEntradas - totalSalidas;

  const handleImprimirAmortizacion = async () => {
    try {
      const doc = <AmortizacionPDF data={amortizacionxlote} idContrato={idContrato} />;
      const blob = await pdf(doc).toBlob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      handleMuestraSnack("Error al generar PDF", "error");
    }
  };

  return (
    <>
      <SnackBarWiiba opensnack={opensnack} tipoSnackBar={typesnackbar} mensaje={message} handleClosesSnack={handleClosesSnack} />
      <Dialog open={modalMov} onClose={handleClose} fullScreen>
        {/* HEADER */}
        <div className={classes.header}>
          <div>
            <p className={classes.headerTitle}>Detalle del Contrato</p>
            {idContrato && <p className={classes.headerSub}>Contrato No. {idContrato}</p>}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Chip className={classes.summaryChip} label={"Total: " + fmtMoney(totalContrato)} size="small"
              style={{ backgroundColor: "#e3f2fd", color: "#1565c0" }} />
            <Chip className={classes.summaryChip} label={"Pagado: " + fmtMoney(totalPagado)} size="small"
              style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }} />
            <Chip className={classes.summaryChip} label={"Pendiente: " + fmtMoney(totalPendiente)} size="small"
              style={{ backgroundColor: "#fff3e0", color: "#e65100" }} />
          </div>
        </div>

        <DialogContent className={classes.content}>
          <Grid container spacing={2}>
            {/* TABLA AMORTIZACIÓN */}
            <Grid item xs={12}>
              <div className={classes.card}>
                <Typography className={classes.sectionTitle}>Tabla de amortización</Typography>
                <Divider className={classes.divider} />
                <MaterialTable
                  title=""
                  columns={columnsAmort}
                  data={amortizacionxlote || []}
                  onRowClick={(_, r) => setSelectedRow(r)}
                  options={{
                    search: true, paging: true, pageSize: 10, toolbar: false,
                    headerStyle: { fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: 11 },
                    rowStyle: r => {
                      if (r.ESTATUS === "A") return { backgroundColor: "#f9f9f9", color: "#aaa" };
                      if (r.ESTATUS === "M") return { backgroundColor: "#ffebee" };
                      return {};
                    }
                  }}
                  localization={{ body: { emptyDataSourceMessage: "Sin datos de amortización" } }}
                />
              </div>
            </Grid>

            {/* TABLA MOVIMIENTOS */}
            <Grid item xs={12}>
              <div className={classes.card}>
                <Typography className={classes.sectionTitle}>Movimientos del lote</Typography>
                <Divider className={classes.divider} />
                <MaterialTable
                  title=""
                  columns={columnsMov}
                  data={movimientosLote || []}
                  onRowClick={(_, r) => setSelectedRow(r)}
                  options={{
                    search: false, paging: true, pageSize: 5, toolbar: false,
                    headerStyle: { fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: 11 }
                  }}
                  localization={{ body: { emptyDataSourceMessage: "Sin movimientos registrados" } }}
                />
                {/* Footer totales */}
                {movimientosLote && movimientosLote.length > 0 && (
                  <div style={{
                    display: "flex", justifyContent: "flex-end", gap: 16,
                    padding: "12px 16px", borderTop: "2px solid #e0e0e0",
                    backgroundColor: "#fafafa", borderRadius: "0 0 8px 8px"
                  }}>
                    <Chip label={"Entradas: " + fmtMoney(totalEntradas)} size="small"
                      style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600, fontSize: 12 }} />
                    <Chip label={"Salidas: " + fmtMoney(totalSalidas)} size="small"
                      style={{ backgroundColor: "#ffebee", color: "#c62828", fontWeight: 600, fontSize: 12 }} />
                    <Chip label={"Saldo: " + fmtMoney(saldoMovimientos)} size="small"
                      style={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 700, fontSize: 12 }} />
                  </div>
                )}
              </div>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions className={classes.actions}>
          <Button onClick={handleClose} color="danger" size="sm">Cerrar</Button>
          <div className={classes.actionsRight}>
            {amortizacionxlote && amortizacionxlote.length > 0 && (
              <Tooltip title="Imprimir tabla de amortización">
                <Button color="info" size="sm" onClick={handleImprimirAmortizacion}>
                  Imprimir amortización
                </Button>
              </Tooltip>
            )}
            {idContrato && (
              <Button color="primary" size="sm"
                onClick={() => { handleClose(); window.location.href = "/admin/Cobranza?contrato=" + idContrato; }}>
                Ir a Cobranza
              </Button>
            )}
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalMovimientosLotes;
